import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import FormInput from "./FormInput";

/**
 * ============================================================
 * PasswordField
 * ============================================================
 *
 * Champ mot de passe avec :
 *
 * - Affichage / masquage du mot de passe
 * - Indicateur de force
 * - Internationalisation
 * - Gestion des erreurs
 * - Gestion des propriétés standard d'un champ
 *
 * ============================================================
 */

export default function PasswordField({

    label,
    icon = "bi bi-lock",
    name = "password",
    value = "",
    onChange,
    onBlur,
    error = "",
    required = false,
    disabled = false,
    readOnly = false,
    autoFocus = false,
    placeholder,
    showStrength = true,
    showToggle = true,
    autoComplete = "new-password",
    col = "col-12",
    labelCol = 4,
    inputCol = 8,
    className = ""

}) {

    // ==========================================================
    // Internationalisation
    // ==========================================================

    const { t } = useTranslation();

    // ==========================================================
    // Valeurs par défaut traduites
    // ==========================================================

    const translatedLabel =
        label ?? t("monProfile.password");

    const translatedPlaceholder =
        placeholder ?? t("monProfile.password");

    // ==========================================================
    // Etat
    // ==========================================================

    const [visible, setVisible] = useState(false);

    // ==========================================================
    // Calcul de la force du mot de passe
    // ==========================================================

    const strength = useMemo(() => {

        let score = 0;

        if (!value) {
            return 0;
        }

        // Au moins 8 caractères
        if (value.length >= 8) {
            score++;
        }

        // Une majuscule
        if (/[A-Z]/.test(value)) {
            score++;
        }

        // Une minuscule
        if (/[a-z]/.test(value)) {
            score++;
        }

        // Un chiffre
        if (/[0-9]/.test(value)) {
            score++;
        }

        // Un caractère spécial
        if (/[^A-Za-z0-9]/.test(value)) {
            score++;
        }

        return score;

    }, [value]);

    // ==========================================================
    // Textes de force du mot de passe
    // ==========================================================

    const strengthText = [

        "",

        t("monProfile.passwordStrength.veryWeak"),

        t("monProfile.passwordStrength.weak"),

        t("monProfile.passwordStrength.medium"),

        t("monProfile.passwordStrength.strong"),

        t("monProfile.passwordStrength.veryStrong")

    ];

    // ==========================================================
    // Couleurs Bootstrap
    // ==========================================================

    const strengthColor = [

        "",

        "danger",

        "warning",

        "info",

        "primary",

        "success"

    ];

    // ==========================================================
    // Affichage
    // ==========================================================

    return (

        <div className={className}>

            <div className="position-relative">

                <FormInput

                    type={visible ? "text" : "password"}

                    label={translatedLabel}

                    icon={icon}

                    name={name}

                    value={value}

                    onChange={onChange}

                    onBlur={onBlur}

                    error={error}

                    required={required}

                    disabled={disabled}

                    readOnly={readOnly}

                    autoFocus={autoFocus}

                    placeholder={translatedPlaceholder}

                    autoComplete={autoComplete}

                    col={col}

                    labelCol={labelCol}

                    inputCol={inputCol}

                />

                {/* ==================================================
                    Bouton afficher / masquer
                    ================================================== */}

                {

                    showToggle &&

                    <button

                        type="button"

                        className="btn btn-link position-absolute"

                        style={{
                            right: 15,
                            top: 37,
                            textDecoration: "none"
                        }}

                        onClick={() =>
                            setVisible((previous) => !previous)
                        }

                        disabled={disabled}

                        aria-label={
                            visible
                                ? t("monProfile.hidePassword")
                                : t("monProfile.showPassword")
                        }

                        title={
                            visible
                                ? t("monProfile.hidePassword")
                                : t("monProfile.showPassword")
                        }

                    >

                        <i

                            className={
                                visible
                                    ? "bi bi-eye-slash"
                                    : "bi bi-eye"
                            }

                        />

                    </button>

                }

            </div>

            {/* ======================================================
                Indicateur de force
                ====================================================== */}

            {

                showStrength && value &&

                <div className="mt-2">

                    <div
                        className="progress"
                        style={{ height: 7 }}
                    >

                        <div

                            className={
                                `progress-bar bg-${strengthColor[strength]}`
                            }

                            style={{
                                width: `${strength * 20}%`
                            }}

                        />

                    </div>

                    <small
                        className={
                            `text-${strengthColor[strength]}`
                        }
                    >

                        {strengthText[strength]}

                    </small>

                </div>

            }

        </div>
    );
}